import React, { useState, useEffect } from 'react';
import styles from './styles.module.scss';
import { EditIcon3 } from 'assets';
import { TechnicalCompetencePayload, useTechnicalCompetencePost, useToast } from 'hooks';
import QuestionModal from 'components/modal-variants/questions-modal';

interface QuestionCategory {
  questions: string[];
  recommended: string;
}

interface QuestionsData {
  question1: QuestionCategory;
  question2: QuestionCategory;
  question3: QuestionCategory;
  question4: QuestionCategory;
  question5: QuestionCategory;
}

const normalizeQuestionsData = (source: any): QuestionsData => {
  const entries = Array.isArray(source)
    ? source
    : source?.questions && typeof source.questions === 'object'
      ? source.questions
      : source?.message && typeof source.message === 'object'
        ? source.message
        : source || {};

  const getQuestion = (question: any) => {
    if (!question) {
      return { questions: [], recommended: '' };
    }

    const questions = Array.isArray(question.questions)
      ? question.questions.filter(Boolean)
      : Array.isArray(question.question)
        ? question.question.filter(Boolean)
        : Array.isArray(question.text)
          ? question.text.filter(Boolean)
          : [];

    return {
      questions,
      recommended:
        question.recommended ||
        question.question ||
        question.text ||
        question.question_content ||
        questions[0] ||
        '',
    };
  };

  const fallback = { questions: [], recommended: '' };
  return {
    question1: getQuestion(entries.question1 || entries.competence1 || entries[0]) || fallback,
    question2: getQuestion(entries.question2 || entries.competence2 || entries[1]) || fallback,
    question3: getQuestion(entries.question3 || entries.competence3 || entries[2]) || fallback,
    question4: getQuestion(entries.question4 || entries.competence4 || entries[3]) || fallback,
    question5: getQuestion(entries.question5 || entries.competence5 || entries[4]) || fallback,
  };
};

interface QuestionsComponentProps {
  questionsData: QuestionsData;
  assessmentId: string | null;
  onQuestionsChange?: (questions: TechnicalCompetencePayload | null) => void;
  disabled?: boolean;
}

const QuestionsComponent: React.FC<QuestionsComponentProps> = ({
  questionsData,
  assessmentId,
  onQuestionsChange,
  disabled
}) => {
  const { openToast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [localQuestionsData, setLocalQuestionsData] = useState<QuestionsData>(() => normalizeQuestionsData(questionsData));
  const {
    loading: postLoading,
    status: postStatus,
    error: postError,
    submitTechnicalCompetence
  } = useTechnicalCompetencePost();

  // Report current questions state to parent whenever it changes
  useEffect(() => {
    if (assessmentId && onQuestionsChange) {
      const safeGetRecommended = (cat: keyof QuestionsData) =>
        localQuestionsData[cat]?.recommended || '';
      const payload: TechnicalCompetencePayload = {
        assessment_id: assessmentId,
        competence1: safeGetRecommended('question1'),
        competence2: safeGetRecommended('question2'),
        competence3: safeGetRecommended('question3'),
        competence4: safeGetRecommended('question4'),
        competence5: safeGetRecommended('question5'),
      };
      onQuestionsChange(payload);
    }
  }, [localQuestionsData, assessmentId, onQuestionsChange]);

  useEffect(() => {
    setLocalQuestionsData(normalizeQuestionsData(questionsData));
  }, [questionsData]);

  const handleEdit = (category: string) => {
    setSelectedCategory(category);
    setIsModalOpen(true);
  };

  const handleSave = (selectedQuestion: string) => {
    if (!selectedCategory || !assessmentId) {
      openToast({
        show: true,
        heading: "Required",
        text: "Assessment ID and category are required",
        type: "error",
        timeOut: 5000,
      });
      return;
    }

    // Update local state
    const updatedQuestionsData = {
      ...localQuestionsData,
      [selectedCategory]: {
        ...localQuestionsData[selectedCategory as keyof QuestionsData],
        recommended: selectedQuestion
      }
    };
    setLocalQuestionsData(updatedQuestionsData);

    // Prepare API payload with defensive checks
    const safeGetRecommended = (cat: keyof QuestionsData) =>
      updatedQuestionsData[cat]?.recommended || '';
    const payload: TechnicalCompetencePayload = {
      assessment_id: assessmentId,
      competence1: safeGetRecommended('question1'),
      competence2: safeGetRecommended('question2'),
      competence3: safeGetRecommended('question3'),
      competence4: safeGetRecommended('question4'),
      competence5: safeGetRecommended('question5'),
    };

    // Submit to API
    try {
      if (postLoading) {
        return;
      }

      submitTechnicalCompetence(payload).then(() => {
        if (postStatus === 201) {
          openToast({
            show: true,
            heading: "Update Successful",
            text: "Technical competence questions updated successfully!",
            type: "info",
            timeOut: 5000,
          });
        } else if (postError) {
          console.error("Submission failed:", postError);
          let errorMessage = "Unknown error";
          if (
            typeof postError === "object" &&
            postError !== null &&
            "response" in postError &&
            typeof (postError as any).response?.data?.message === "string"
          ) {
            errorMessage = (postError as any).response.data?.message;
          }
          openToast({
            show: true,
            heading: "Update Failed",
            text: `Submission failed: ${errorMessage}`,
            type: "error",
            timeOut: 5000,
          });
          // Revert local state on error
          setLocalQuestionsData(questionsData);
        }
      }).catch((error) => {
        console.error("Unexpected error during submission:", error);
        openToast({
          show: true,
          heading: "Update Failed",
          text: "An unexpected error occurred. Please try again.",
          type: "error",
          timeOut: 5000,
        });
        // Revert local state on error
        setLocalQuestionsData(questionsData);
      });
    } catch (error) {
      console.error("Error preparing submission:", error);
      openToast({
        show: true,
        heading: "Update Failed",
        text: "Failed to prepare submission. Please check your data and try again.",
        type: "error",
        timeOut: 5000,
      });
      // Revert local state on error
      setLocalQuestionsData(questionsData);
    }
  };

  return (
    <section className={styles.questionsSection}>
      {Object.entries(localQuestionsData).map(([key, value], index) => {
        const recommended = value?.recommended || value?.questions?.[0] || '';
        return (
          <div key={index} className={styles.questionItem}>
            <div className={styles.questionNumber}>{index + 1}.</div>
            <div className={styles.questionText}>{recommended}</div>
            {!disabled && <EditIcon3 className={styles.editIcon} onClick={() => handleEdit(key)} />}
          </div>
        );
      })}
      <QuestionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        selectedCategory={selectedCategory}
        questionsData={localQuestionsData}
      />
    </section>
  );
};

export default QuestionsComponent;